import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Tooltip, 
  Slider,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Today as TodayIcon,
  Info as InfoIcon,
  ViewDay as DayViewIcon,
  ViewTimeline as TimelineIcon
} from '@mui/icons-material';
import { format, parseISO, differenceInDays, addDays, isToday, isSameMonth, set, startOfWeek, endOfWeek, eachDayOfInterval, addHours } from 'date-fns';
import { eventTypeGradients } from './styles/eventStyles';

const getEventGradient = (eventType) => {
  // Default gradient if type doesn't match
  const defaultGradient = {
    gradient: 'linear-gradient(135deg, rgba(128, 128, 128, 0.2) 0%, rgba(96, 96, 96, 0.2) 100%)',
    border: 'rgba(128, 128, 128, 0.3)',
    hover: 'linear-gradient(135deg, rgba(128, 128, 128, 0.3) 0%, rgba(96, 96, 96, 0.3) 100%)'
  };

  // Check if event type exists and has a gradient
  if (!eventType || !eventTypeGradients[eventType]) {
    console.warn(`Unknown event type: ${eventType}, using default gradient`);
    return defaultGradient.gradient;
  }

  return eventTypeGradients[eventType].gradient;
};

const TimelineGanttView = ({ events = [], onEventClick }) => {
  const [zoom, setZoom] = useState(40);
  const [showDetails, setShowDetails] = useState(false);
  const [viewMode, setViewMode] = useState('gantt');

  const handleViewChange = (_, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // Group events by project
  const projectGroups = events.reduce((groups, event) => {
    const projectId = event.projectId || 'ungrouped';
    if (!groups[projectId]) {
      groups[projectId] = {
        name: event.projectName || 'Other Events',
        events: []
      };
    }
    groups[projectId].events.push(event);
    return groups;
  }, {});

  const timelineStart = events.length > 0 
    ? parseISO(events.reduce((earliest, event) => 
        event.startDate < earliest ? event.startDate : earliest
      , events[0].startDate))
    : new Date();
  
  const timelineEnd = events.length > 0
    ? addDays(parseISO(events.reduce((latest, event) => 
        (event.endDate || event.startDate) > latest ? (event.endDate || event.startDate) : latest
      , events[0].startDate)), 14)
    : addDays(new Date(), 30);

  const totalDays = differenceInDays(timelineEnd, timelineStart) + 1;

  const renderControls = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'linear-gradient(90deg, rgba(0, 188, 212, 0.05) 0%, rgba(32, 178, 170, 0.05) 100%)'
    }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              '&.Mui-selected': {
                color: '#00BCD4',
                background: 'rgba(0, 188, 212, 0.1)',
                borderColor: '#00BCD4'
              },
              '&:hover': {
                background: 'rgba(0, 188, 212, 0.05)'
              }
            }
          }}
        >
          <ToggleButton value="gantt" aria-label="gantt view">
            <TimelineIcon />
          </ToggleButton>
          <ToggleButton value="day" aria-label="day view">
            <DayViewIcon />
          </ToggleButton>
          <ToggleButton value="week" aria-label="week view">
            <TimelineIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={() => setZoom(Math.max(20, zoom - 10))} sx={{ color: '#fff' }}>
            <ZoomOutIcon />
          </IconButton>
          <Slider
            value={zoom}
            onChange={(_, newValue) => setZoom(newValue)}
            min={20}
            max={100}
            sx={{ 
              width: '150px',
              '& .MuiSlider-thumb': {
                backgroundColor: '#7C4DFF'
              },
              '& .MuiSlider-track': {
                background: 'linear-gradient(90deg, #00BCD4 0%, #20B2AA 100%)'
              }
            }}
          />
          <IconButton onClick={() => setZoom(Math.min(100, zoom + 10))} sx={{ color: '#fff' }}>
            <ZoomInIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Jump to Today">
          <IconButton 
            onClick={() => {
              const todayOffset = differenceInDays(new Date(), timelineStart) * zoom;
              document.getElementById('timeline-container').scrollLeft = todayOffset - 100;
            }}
            sx={{ color: '#fff' }}
          >
            <TodayIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Toggle Details">
          <IconButton 
            onClick={() => setShowDetails(!showDetails)}
            sx={{ color: '#fff' }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderTimelineGrid = () => (
    <Box sx={{ 
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none'
    }}>
      {Array.from({ length: totalDays }).map((_, index) => (
        <Box
          key={`grid-${index}`}
          sx={{
            position: 'absolute',
            left: `${index * zoom}px`,
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          }}
        />
      ))}
    </Box>
  );

  const renderDayView = () => {
    const today = new Date();
    const dayEvents = events.filter(event => 
      format(parseISO(event.startDate), 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
    );

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          {format(today, 'EEEE, MMMM d, yyyy')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 24 }).map((_, hour) => (
            <Box
              key={hour}
              sx={{
                display: 'flex',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                minHeight: '60px',
                position: 'relative'
              }}
            >
              <Typography
                sx={{
                  width: '60px',
                  color: '#fff',
                  opacity: 0.7,
                  fontSize: '0.875rem'
                }}
              >
                {format(set(today, { hours: hour, minutes: 0 }), 'HH:mm')}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative' }}>
                {dayEvents
                  .filter(event => {
                    const eventHour = parseISO(event.startDate).getHours();
                    return eventHour === hour;
                  })
                  .map(event => (
                    <Box
                      key={event.id}
                      onClick={() => onEventClick?.(event)}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: '4px',
                        background: getEventGradient(event.type),
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          transition: 'transform 0.2s'
                        }
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                        {event.title}
                      </Typography>
                      <Typography sx={{ color: '#fff', opacity: 0.7, fontSize: '0.75rem' }}>
                        {format(parseISO(event.startDate), 'HH:mm')}
                        {event.endDate && ` - ${format(parseISO(event.endDate), 'HH:mm')}`}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(new Date());
    const weekEnd = endOfWeek(new Date());
    const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto'
      }}>
        {/* Week Header */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '80px repeat(7, 1fr)',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Box sx={{ p: 1 }} /> {/* Time column header */}
          {weekDays.map(day => (
            <Box
              key={day.toString()}
              sx={{
                p: 2,
                textAlign: 'center',
                borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                background: isToday(day) 
                  ? 'linear-gradient(180deg, rgba(0, 188, 212, 0.1) 0%, rgba(32, 178, 170, 0.1) 100%)'
                  : 'transparent'
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography sx={{ 
                color: isToday(day) ? '#00BCD4' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem'
              }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Time Grid */}
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          {/* Hour Rows */}
          {Array.from({ length: 24 }).map((_, hour) => (
            <Box
              key={hour}
              sx={{
                display: 'grid',
                gridTemplateColumns: '80px repeat(7, 1fr)',
                minHeight: '60px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.02)'
                }
              }}
            >
              {/* Time Label */}
              <Box sx={{
                p: 1,
                borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem'
                }}>
                  {format(set(new Date(), { hours: hour, minutes: 0 }), 'HH:mm')}
                </Typography>
              </Box>

              {/* Day Columns */}
              {weekDays.map(day => {
                const currentSlotEvents = events.filter(event => {
                  const eventDate = parseISO(event.startDate);
                  return (
                    format(eventDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
                    eventDate.getHours() === hour
                  );
                });

                return (
                  <Box
                    key={day.toString()}
                    sx={{
                      borderLeft: '1px solid rgba(255, 255, 255, 0.05)',
                      p: 0.5,
                      position: 'relative',
                      minHeight: '60px'
                    }}
                  >
                    {currentSlotEvents.map(event => (
                      <Box
                        key={event.id}
                        onClick={() => onEventClick?.(event)}
                        sx={{
                          position: 'absolute',
                          left: '4px',
                          right: '4px',
                          minHeight: '28px',
                          background: getEventGradient(event.type),
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          zIndex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {event.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.7rem'
                          }}
                        >
                          {format(parseISO(event.startDate), 'HH:mm')}
                          {event.endDate && ` - ${format(parseISO(event.endDate), 'HH:mm')}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Current Time Indicator */}
          {isToday(new Date()) && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${(new Date().getHours() * 60 + new Date().getMinutes()) * (60 / 60)}px`,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            >
              <Box
                sx={{
                  height: '2px',
                  background: 'linear-gradient(90deg, #00BCD4 0%, #20B2AA 100%)',
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '80px',
                    top: '-4px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#00BCD4'
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper sx={{ 
      backgroundColor: 'rgba(18, 18, 18, 0.95)',
      height: 'calc(100vh - 300px)',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 2
    }}>
      {renderControls()}
      
      {viewMode === 'day' ? renderDayView() : viewMode === 'week' ? renderWeekView() : (
        <Box 
          id="timeline-container"
          sx={{ 
            overflowX: 'auto',
            overflowY: 'auto',
            flex: 1,
            position: 'relative'
          }}
        >
          {/* Project Groups */}
          {Object.entries(projectGroups).map(([projectId, project], groupIndex) => (
            <Box
              key={projectId}
              sx={{
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative',
                minHeight: `${project.events.length * 50 + 40}px`,
                background: groupIndex % 2 === 0 
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'transparent'
              }}
            >
              <Typography
                sx={{
                  position: 'sticky',
                  left: 0,
                  padding: '8px 16px',
                  backgroundColor: 'rgba(18, 18, 18, 0.95)',
                  color: '#fff',
                  zIndex: 1,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'linear-gradient(90deg, rgba(0, 188, 212, 0.1) 0%, rgba(32, 178, 170, 0.1) 100%)'
                }}
              >
                {project.name}
              </Typography>

              {project.events.map((event, eventIndex) => {
                const startOffset = differenceInDays(parseISO(event.startDate), timelineStart);
                const duration = event.endDate 
                  ? differenceInDays(parseISO(event.endDate), parseISO(event.startDate)) + 1
                  : 1;

                return (
                  <Box
                    key={event.id}
                    onClick={() => onEventClick?.(event)}
                    sx={{
                      position: 'absolute',
                      left: `${startOffset * zoom}px`,
                      width: `${duration * zoom}px`,
                      top: `${eventIndex * 50 + 40}px`,
                      height: '35px',
                      background: getEventGradient(event.type),
                      borderRadius: '17px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {event.title}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}

          {renderTimelineGrid()}
        </Box>
      )}
    </Paper>
  );
};

export default TimelineGanttView; 